import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { HelpCircle, ChevronDown, MessageCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const faqs = [
  {
    question: "How do I find a MILE PLUS charging station near me?",
    answer:
      "You can find our stations through the MILE PLUS mobile app (available on iOS and Android), our website's station finder, or Google Maps. All our stations show real-time availability status so you can plan your charging stops efficiently.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "We accept multiple payment options including UPI (PhonePe, Google Pay, Paytm), credit/debit cards, and our in-app MILE PLUS Wallet. The MILE PLUS Wallet offers additional benefits like cashback and faster checkout.",
  },
  {
    question: "How long does it take to charge my EV?",
    answer:
      "Charging time depends on your vehicle's battery capacity and the charger type. Our DC Fast chargers (25-60 kW) can charge most EVs to 80% in 45-90 minutes. AC chargers (3.3-7.2 kW) take 6-8 hours for a full charge, ideal for overnight charging.",
  },
  {
    question: "What types of charging connectors do you support?",
    answer:
      "Our stations support all major connector types including CCS2 (most common for Indian EVs), CHAdeMO, and Type 2 AC. Most of our DC fast chargers come with CCS2 connectors, while AC stations have Type 2 connectors compatible with all Indian EVs.",
  },
  {
    question: "Are your charging stations available 24/7?",
    answer:
      "Yes! All MILE PLUS charging stations operate 24 hours a day, 7 days a week. You can charge your EV at any time that's convenient for you. Some stations at partner locations may have specific access hours - check the app for details.",
  },
  {
    question: "What happens if a charging station is not working?",
    answer:
      "We maintain high uptime across our network. If you encounter an issue, report it through the app and we'll prioritize repairs. You'll also see alternative nearby stations in the app. Our support team is available 24/7 at +91 7030808257.",
  },
  {
    question: "Can I reserve a charging slot in advance?",
    answer:
      "Currently, charging is available on a first-come, first-served basis. However, our app shows real-time availability so you can plan accordingly. We're working on a reservation feature for premium users.",
  },
  {
    question: "Do you offer home charging solutions?",
    answer:
      "Yes! We provide home charging installation services including site assessment, charger procurement, and professional installation. Contact us through the app or website to schedule a consultation for home or office charging solutions.",
  },
];

const FAQ = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });
  const [openItems, setOpenItems] = useState([]);

  return (
    <section
      id="faq"
      className="py-24 md:py-32 bg-background-paper relative overflow-hidden"
      data-testid="faq-section"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24" ref={containerRef}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            FAQ
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4">
            GOT QUESTIONS?
            <br />
            <span className="text-primary">WE'VE GOT ANSWERS</span>
          </h2>
          <p className="text-foreground-muted text-lg mt-6">
            Everything you need to know about EV charging with MILE PLUS.
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Accordion type="multiple" value={openItems} onValueChange={setOpenItems}>
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border-b border-white/10"
              >
                <AccordionTrigger
                  className="py-6 text-left hover:no-underline group"
                  data-testid={`faq-trigger-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <HelpCircle className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-medium text-foreground text-lg pr-8 group-hover:text-primary transition-colors">
                      {faq.question}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pl-12 pb-6">
                  <p className="text-foreground-muted leading-relaxed">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Still Have Questions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center glass-card"
        >
          <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-6">
            <MessageCircle className="w-8 h-8 text-secondary" />
          </div>
          <h3 className="font-heading text-2xl text-foreground mb-3">
            Still Have Questions?
          </h3>
          <p className="text-foreground-muted mb-6">
            Can't find the answer you're looking for? Our support team is here to
            help.
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
            data-testid="faq-contact-link"
          >
            Contact Support
            <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
